import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Button } from '@cimpress/react-components';
import { connect } from 'react-redux';
import { getAllSkuData } from 'src/selectors/skus';
import {
    retrieveVersionsForSku,
    retrieveProductDetails,
    cleanSceneConfiguration,
} from 'src/pages/editor/components/SceneVariation/slice';
import { VersionSelector } from '@rendering/components';
import { getSurfaceProperties } from 'src/selectors/pages/editor';
import type { RootState } from 'src/store';
import type { SurfaceStateType } from 'src/types/surface';
import type { SurfaceSliceState } from 'src/pages/editor/components/surfaces/slice';
import type { ProductVersion } from 'src/types/product';
import AttributesSelection from './AttributesSelection';
import getSceneVariation from './selectors/getSceneVariation';

interface Props {
    allSkus: Record<string, SurfaceStateType>;
    surfaces: SurfaceSliceState;
    // The underlying reducer takes no payload (RTK types the created action creator's parameter as
    // `void`), but this component calls it positionally with `sku` anyway (the reducer ignores it) --
    // `any` is required here since no stricter parameter type is assignable from a `void`-parameter
    // action creator.
    retrieveVersionsForSku: (sku: any) => void;
    retrieveProductDetails: (payload: {
        sku: string;
        skuVersion?: number | string;
        cimSku?: string;
        cimSkuVersion?: number;
    }) => void;
    reset: () => void;
    configuredSku: string;
    configuredSkuVersion: number;
    cimSku: string;
    cimSkuVersion: number;
    sceneVariableId?: string;
}

const SkuInput = (props: Props) => {
    const [sku, setSku] = useState('');
    // Widened to `number | string` because `onReset` below (pre-existing behavior) resets this to
    // `''` rather than `0`.
    const [skuVersion, setSkuVersion] = useState<number | string>(0);
    const [showConfig, setShowConfig] = useState(false);
    const { sku: sku2, skuVersion: version, merchantSku, merchantVersion } = props.surfaces;

    useEffect(() => {
        if (props.configuredSku) {
            setSku(props.configuredSku);
            setSkuVersion(props.configuredSkuVersion);
            setShowConfig(true);
        } else if (props.surfaces && props.surfaces.isReady) {
            props.retrieveProductDetails({
                sku: merchantSku || sku2,
                skuVersion: merchantVersion || version,
                cimSku: merchantSku || sku2,
                cimSkuVersion: merchantVersion || version,
            });
            setShowConfig(true);
        }
    }, [props.surfaces, props.configuredSku, props.configuredSkuVersion]);

    const onChange = (event: React.ChangeEvent<HTMLInputElement> & { isValid: boolean }) => {
        setSku(event.target.value.trim());
        setSkuVersion(0);
        setShowConfig(false);
    };

    const getProductVersions = () => {
        if (sku) {
            props.retrieveVersionsForSku(sku);
        }
    };

    const onChangeVersion = (newVersion: number) => {
        setShowConfig(false);
        setSkuVersion(newVersion);
    };

    const onReset = () => {
        setSku('');
        setSkuVersion('');
        props.reset();
    };

    const productDetails = useMemo(() => {
        let details: SurfaceStateType;
        if (props.cimSkuVersion) {
            // `allSkus[key]` falls back to `{}` when the sku hasn't been fetched into state yet;
            // asserting the real shape here since the `ruleSet`/`options` reads below tolerate it
            // being undefined on an otherwise-real `SurfaceStateType`.
            details = (props.allSkus[`${props.cimSku}-${props.cimSkuVersion}`] || {}) as SurfaceStateType;
        } else {
            details = (props.allSkus[props.cimSku] || {}) as SurfaceStateType;
        }
        return details.ruleSet || details.options ? details : null;
    }, [props.allSkus, props.cimSku, props.cimSkuVersion]);

    const versions = useMemo(() => {
        if (sku) {
            const skuData = (props.allSkus[sku] || props.allSkus[`${sku}-${skuVersion}`] || {}) as SurfaceStateType;
            // `SurfaceStateType.versions` is declared as `string[]`, but it's always populated with the
            // version objects produced by `productClient.ts`'s `mapProductVersion` (see also `sku.tsx`'s
            // equivalent `(v: any)` workaround for the same gap) -- asserting the real shape here instead
            // of leaving it `any`.
            const data = (skuData.versions || []) as unknown as ProductVersion[];
            return data.map((v) => {
                const status = v.status?.toLowerCase() || '';
                const statusLabel: Record<string, string> = {
                    retired: ' (retired)',
                    error: ' (error)',
                };
                return {
                    value: `${v.version}`,
                    label: `${v.version}${v.current ? ' (current)' : ''}${statusLabel[status] ?? ''}`,
                    status,
                    isCurrent: !!v.current,
                };
            });
        }
        return [];
    }, [props.allSkus, sku, skuVersion, productDetails]);

    const btnText = useMemo(() => (sku && versions.length ? 'Load' : 'Verify'), [sku, versions]);

    const onHandleAction = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (sku && versions.length) {
            setShowConfig(true);
            props.retrieveProductDetails({ sku, skuVersion });
        } else {
            getProductVersions();
        }
    };

    const hasData = sku && productDetails && showConfig;

    return (
        <div>
            <form className='sku-input' onSubmit={onHandleAction}>
                <TextField
                    className='choose-sku__input'
                    value={sku}
                    onChange={onChange}
                    disabled={!!props.sceneVariableId}
                    label='Product SKU'
                    type='text'
                />
                {versions && (
                    // TODO: widthUnit has to be removed from DOM attributes for this component
                    <VersionSelector
                        versions={versions}
                        value={skuVersion as number}
                        disabled={!!props.sceneVariableId}
                        onVersionSelected={onChangeVersion}
                    />
                )}
                <Button disabled={props.allSkus[sku]?.fetching || !!props.sceneVariableId}>{btnText}</Button>
                {hasData && !props.sceneVariableId && (
                    <div className='sku-input__extra'>
                        <Button variant='anchor' onClick={onReset}>
                            Change Product Info
                        </Button>
                    </div>
                )}
            </form>
            {hasData && productDetails && (
                <AttributesSelection productDetails={productDetails} sku={sku} skuVersion={skuVersion as number} />
            )}
        </div>
    );
};

function mapStateToProps(state: RootState) {
    const skus = getAllSkuData(state);
    const {
        sku: configuredSku,
        skuVersion: configuredSkuVersion,
        cimSku,
        cimSkuVersion,
        sceneVariableId,
    } = getSceneVariation(state);

    return {
        allSkus: skus,
        surfaces: getSurfaceProperties(state),
        configuredSku,
        configuredSkuVersion,
        cimSku,
        cimSkuVersion,
        sceneVariableId,
    };
}

const skuConfiguration = connect(mapStateToProps, {
    retrieveVersionsForSku,
    retrieveProductDetails,
    reset: cleanSceneConfiguration,
})(SkuInput);

export default skuConfiguration;
